"use client";
import { RecommendProductItem } from "@/apiUtils/algoliaUtils/types";
import { hasArrayElements } from "@/apiUtils/commonUtils";
import AngleLeftIcon from "@/assets/icons/arrow-left.svg?react";
import AngleRightIcon from "@/assets/icons/arrow-right.svg?react";
import NoImageSrc from "@/assets/images/no_image.svg";
import { LinkContent, RecommendationPromotionTileData, TranslationData } from "@/common/lib/types";
import Image from "@/components/ui/Image/Image";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { CLink } from "../common/customLink/CustomLink";
import ProductWithData from "../productcard/ProductWithData";
import { Carousel, Slide } from "../ui/carousel/Carousel";
import { SanityImg } from "../ui/SanityImg/SanityImg";
import Button from "../ui/button/Button";
import { useCallback, useRef } from "react";
import useDebounceCallback from "@/hooks/useDebounceCallback";
import { useGlobalStore } from "@/store/store";
import { algoliaViewedObjectIDsListClient } from "@/apiUtils/algoliaUtils/indexClient";
import { eventParam } from "@/apiUtils/algoliaUtils/constants";
import { AlgoliaProductItem } from "@/apiUtils/algoliaUtils/types";
import { analyticEventTypes } from "@/apiUtils/constants";
import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";

interface IProps {
  products?: RecommendProductItem[];
  title?: string;
  backgroundColor?: string;
  cta?: {
    title: string;
    link: LinkContent;
  };
  card?: {
    promotionTile: RecommendationPromotionTileData;
  };
  translations: TranslationData;
  tryOn: boolean;
  parentContainer?: boolean;
  source?: string;
  sourceId?: string;
  showCartAddedState?: boolean;
  isChevronVariant?: boolean;
  onDeferProductToast?: (imgUrl: string) => void;
}

const ProductCarousel = ({
  products,
  title,
  backgroundColor,
  cta,
  card,
  translations,
  tryOn,
  parentContainer = true,
  source,
  sourceId,
  showCartAddedState,
  isChevronVariant = true,
  onDeferProductToast,
}: IProps) => {
  // global store
  const { user } = useGlobalStore((state) => state);

  // refs
  const gaDataRef = useRef<AlgoliaProductItem[]>([]);

  // hooks
  const debounceCallback = useDebounceCallback(99);

  // consts
  const showCard = card?.promotionTile !== undefined && card?.promotionTile?.media != "";

  // methods
  const triggerAnalyticsProductImpression = (visibleProductsData: AlgoliaProductItem[]) => {
    handlePostAnalytics({
      eventType: analyticEventTypes.DETAILS,
      data: {
        target: analyticEvents.VIEW_ITEM_LIST,
        products: visibleProductsData,
        source,
      },
    });

    const indexName = visibleProductsData[0]?.indexName;
    const objectIDs = visibleProductsData.map((item) => item.objectID || (item as any).variantId);
    if (indexName && hasArrayElements(objectIDs)) {
      algoliaViewedObjectIDsListClient(
        indexName,
        eventParam.PRODUCT_VIEW,
        objectIDs,
        user?.magrabi_id,
      ).catch((e) => {
        console.error("Error while Algolia Product Impression Event : ", e);
      });
    }
  };

  const gaTriggerHandler = () => {
    triggerAnalyticsProductImpression(gaDataRef.current);
    gaDataRef.current = [];
  };

  const handleVisible = (isVisible: boolean, data: AlgoliaProductItem) => {
    if (isVisible) {
      gaDataRef.current.push(data);
      debounceCallback(gaTriggerHandler);
    }
  };

  function renderSlides() {
    const productsDataForCarousel = products?.map((prod, i) => {
      return {
        slideClasses: `${showCard ? "md:basis-[253px]" : "md:basis-[251px]"}  basis-[184px]`,
        content: (
          <ProductWithData
            translations={translations}
            product={prod}
            currency={prod.defaultVariant.currency}
            isChevronVariant={isChevronVariant}
            showProductCarousel={false}
            index={i}
            tryOn={tryOn}
            source={source}
            sourceId={sourceId}
            handleVisible={handleVisible}
            showCartAddedState={showCartAddedState}
            onDeferToast={onDeferProductToast}
          />
        ),
      } as Slide;
    });

    if (showCard) {
      return [
        {
          slideClasses: "basis-[184px] md:hidden",
          content: (
            <CLink
              href={card.promotionTile?.link?.url ?? "/"}
              className="flex w-[184px] flex-col overflow-hidden"
            >
              <div className="mb-5 h-[220px]">
                <Image
                  src={
                    card.promotionTile?.media
                      ? getCloudinaryURL(card.promotionTile?.media)
                      : NoImageSrc
                  }
                  alt={card.promotionTile?.title}
                  width={184}
                  height={200}
                  className="h-full object-cover"
                />
              </div>
              <h3 className="font-bold">{card.promotionTile?.title}</h3>
              <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm underline">
                {card.promotionTile?.linkTitle}
              </p>
            </CLink>
          ),
        } as Slide,
        ...(productsDataForCarousel as Slide[]),
      ];
    } else {
      return productsDataForCarousel;
    }
  }

  return (
    <section className={cn("relative px-4 md:px-12", parentContainer ? "container" : "")}>      
    {title && (
        <h3
          className={cn(
            "text-center font-semibold",
            backgroundColor
              ? "mb-8 md:mb-16 lg:mb-[72px] flex min-h-9 w-full items-center justify-center text-sm leading-none md:min-h-14 md:text-base md:leading-normal"
              : (products && products.length > 0) || card?.promotionTile?.media
                ? "mb-6 text-base md:mb-6"
                : "",
          )}
          style={backgroundColor ? { backgroundColor } : undefined}
        >
          {title}
        </h3>
      )}
      {(products && products.length > 0) || card?.promotionTile?.media ? (
        <div className={cn(showCard ? "flex justify-start" : "grid grid-cols-1")}>
          {showCard ? (
            <CLink
              href={card?.promotionTile?.link?.url ?? "/"}
              className="hidden flex-col overflow-hidden md:flex md:w-[252px]"
            >
              <div className="mb-5 md:h-[303px]">
                <SanityImg
                  src={card?.promotionTile?.media ?? NoImageSrc}
                  alt={card?.promotionTile?.title}
                  width={252}
                  height={303}
                  className="h-full object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold">{card?.promotionTile?.title}</h3>
              <p className="overflow-hidden text-ellipsis whitespace-nowrap pt-[6px] text-lg underline">
                {card?.promotionTile?.linkTitle}
              </p>
            </CLink>
          ) : (
            <></>
          )}

          <div
            className={cn(
              "flex-1",
              showCard ? "overflow-hidden md:pl-[83px] md:pr-14 rtl:md:pl-14 rtl:md:pr-[83px]" : "",
            )}
          >
            <Carousel
              paginationControls={{}}
              forceActive
              carouselOptions={{ dragFree: true }}
              autoPlay={{
                playOnInit: false,
              }}
              spacing={showCard ? 16 : 22}
              slides={renderSlides() ?? []}
              {...(products &&
                hasArrayElements(products) && {
                controls: {
                  distance: showCard ? "-54px" : "-34px",
                  left: {
                    top: "35%",
                    content: (
                      <div className="hidden p-2 md:block">
                        <AngleLeftIcon />
                      </div>
                    ),
                  },
                  right: {
                    top: "35%",
                    content: (
                      <div className="hidden p-2 md:block">
                        <AngleRightIcon />
                      </div>
                    ),
                  },
                },
              })}
            />
          </div>
        </div>
      ) : (
        <></>
      )}
      {cta?.title && cta?.link?.url && (
        <div className="mt-8 flex justify-center md:mt-6">
          <CLink href={cta.link.url}>
            <Button
              variant="outlined"
              className="flex h-12 min-w-[224px] items-center justify-center text-sm font-medium"
            >
              {cta.title}
            </Button>
          </CLink>
        </div>
      )}
    </section>
  );
};

export default ProductCarousel;
